import { Table } from "components";
import { detailsCellHeader } from "components/DetailsCellHeader";
import { ExecutionStatusChip } from "features/shared/components/ExecutionStatusChip";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { convertDatetimeStringToDate } from "utils/date";
import { valueOrAlternative } from "utils/namesManipulation";

import type { ProcessingJob } from "../../model/types";

import { formatDurationToMinutesAndSeconds } from "../../../shared/lib/formatters";
import { processingJobHasDetails } from "../../lib/processingJob";

type ProcessingJobsTableProps = {
  className?: string;
  items: ProcessingJob[];
  onDetailsClick: (item: ProcessingJob) => void;
};

export const ProcessingJobsTable = ({ className, items, onDetailsClick }: ProcessingJobsTableProps) => {
  const { t } = useTranslation();

  const handleDetailsClick = useCallback(
    (row: ProcessingJob) => {
      if (!processingJobHasDetails(row)) return;
      onDetailsClick(row);
    },
    [onDetailsClick]
  );

  const headers = useMemo(
    () => [
      {
        dataKey: "algorithm",
        label: t("general.algorithm"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => valueOrAlternative(row.algorithm),
      },
      {
        dataKey: "user_email",
        label: t("general.user"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => valueOrAlternative(row.user?.email),
      },
      {
        dataKey: "status",
        label: t("general.status"),
        valueTransformation: (row: ProcessingJob) => <ExecutionStatusChip status={row.status} />,
      },
      {
        dataKey: "executed_at",
        label: t("general.executedAt"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => convertDatetimeStringToDate(row.executed_at),
      },
      {
        dataKey: "duration_seconds",
        label: t("general.duration"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => formatDurationToMinutesAndSeconds(row.duration_seconds),
      },
      {
        dataKey: "server_label",
        label: t("general.server"),
        valueClassName: "font-ds-body-regular-xs text-lds-foreground-secondary",
        valueTransformation: (row: ProcessingJob) => valueOrAlternative(row.details?.server_label),
      },
      detailsCellHeader<ProcessingJob>({}, handleDetailsClick, processingJobHasDetails),
    ],
    [handleDetailsClick, t]
  );

  return (
    <Table
      className={className}
      data={items}
      editable={() => false}
      headers={headers}
      onRowClick={handleDetailsClick}
      rowRemoveable={() => false}
    />
  );
};
